import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Evento recebido via WebSocket do Traccar e armazenado localmente para
 * formar o feed de notificações do app. Persistido em localStorage para
 * sobreviver a reload da página. Limitado a `MAX_ITEMS` para não estourar
 * o limite de 5 MB do localStorage.
 */
export interface FeedEvent {
  /** id Traccar do evento (pode repetir entre reconexões, daí o uid local) */
  uid: string;
  id: number;
  type: string;
  serverTime: string;
  deviceId?: number;
  positionId?: number;
  geofenceId?: number;
  maintenanceId?: number;
  attributes?: Record<string, unknown>;
  read: boolean;
}

interface EventsState {
  items: FeedEvent[];
  push: (events: Omit<FeedEvent, 'uid' | 'read'>[]) => void;
  markRead: (uids: string[]) => void;
  markAllRead: () => void;
  clear: () => void;
}

const MAX_ITEMS = 200;

export const useEventsStore = create<EventsState>()(
  persist(
    (set) => ({
      items: [],
      push: (events) =>
        set((state) => {
          if (!events.length) return state;
          const existingKeys = new Set(state.items.map((e) => `${e.id}:${e.serverTime}`));
          const incoming: FeedEvent[] = [];
          for (const e of events) {
            const key = `${e.id}:${e.serverTime}`;
            if (existingKeys.has(key)) continue;
            incoming.push({
              ...e,
              uid: `${e.id}-${e.serverTime}-${Math.random().toString(36).slice(2, 8)}`,
              read: false,
            });
          }
          if (incoming.length === 0) return state;
          // mais recentes primeiro
          const next = [...incoming, ...state.items]
            .sort((a, b) => new Date(b.serverTime).getTime() - new Date(a.serverTime).getTime())
            .slice(0, MAX_ITEMS);
          return { items: next };
        }),
      markRead: (uids) =>
        set((state) => {
          const set = new Set(uids);
          return {
            items: state.items.map((e) => (set.has(e.uid) ? { ...e, read: true } : e)),
          };
        }),
      markAllRead: () =>
        set((state) => ({
          items: state.items.map((e) => (e.read ? e : { ...e, read: true })),
        })),
      clear: () => set({ items: [] }),
    }),
    { name: 'fismatek-events-feed' },
  ),
);

/** Seletor: número de não-lidas. Use com store hook para reatividade. */
export function selectUnreadCount(state: EventsState): number {
  let n = 0;
  for (const e of state.items) if (!e.read) n += 1;
  return n;
}
